import dayjs, { Dayjs } from 'dayjs';
import { IGroupedWorkTimes, IWorkTime } from '../interface/workTimes';
import { calculateDuration } from './utilityFunctions';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import minMax from 'dayjs/plugin/minMax';
import duration from 'dayjs/plugin/duration';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(minMax);
dayjs.extend(duration);

export const groupWorkTimesForWeeklyView = (
  calendarData: IWorkTime[],
  startOfWeekDate: Date,
): IGroupedWorkTimes[] => {
  if (!startOfWeekDate) return [];

  const startOfWeek = dayjs(startOfWeekDate).startOf('day');
  const endOfWeek = startOfWeek.add(6, 'day');

  return Object.values(
    calendarData.reduce((acc: Record<string, IGroupedWorkTimes>, elem: IWorkTime) => {
      const segments = [elem];

      segments.forEach((segment) => {
        let startDate = dayjs(segment.startTime).startOf('day');
        const endDate = dayjs(segment.endTime).startOf('day');

        if (endDate.isBefore(startOfWeek) || startDate.isAfter(endOfWeek)) {
          return;
        }

        while (startDate.isSameOrBefore(endDate)) {
          if (startDate.isBefore(startOfWeek) || startDate.isAfter(endOfWeek)) {
            startDate = startDate.add(1, 'day');
            continue;
          }

          const elemKey = startDate.format('DD');
          if (!acc[elemKey]) {
            acc[elemKey] = {
              date: startDate.toISOString(),
              workTimes: [],
              amount: 0,
              totalDuration: { value: '0', unit: 'minutes' },
            };
          }

          const dayStart = startDate.startOf('day');
          const dayEnd = startDate.endOf('day');

          const workStart = dayjs.max(dayjs(segment.startTime), dayStart);
          const workEnd = dayjs.min(dayjs(segment.endTime), dayEnd);

          const duration = calculateDuration(workStart.toISOString(), workEnd.toISOString());

          let totalMinutes = parseInt(acc[elemKey].totalDuration.value, 10);
          if (acc[elemKey].totalDuration.unit === 'hours') {
            const [hours, minutes] = acc[elemKey].totalDuration.value.split(':').map(Number);
            totalMinutes = hours * 60 + (minutes || 0);
          }

          let newDurationMinutes = parseInt(duration.value, 10);
          if (duration.unit === 'hours') {
            const [hours, minutes] = duration.value.split(':').map(Number);
            newDurationMinutes = hours * 60 + (minutes || 0);
          }

          totalMinutes += newDurationMinutes;

          if (totalMinutes < 60) {
            acc[elemKey].totalDuration = { value: totalMinutes.toString(), unit: 'minutes' };
          } else {
            const totalHours = Math.floor(totalMinutes / 60);
            const remainingMinutes = totalMinutes % 60;
            acc[elemKey].totalDuration = {
              value:
                remainingMinutes === 0
                  ? totalHours.toString()
                  : `${totalHours}:${remainingMinutes < 10 ? '0' : ''}${remainingMinutes}`,
              unit: 'hours',
            };
          }

          acc[elemKey].amount += 1;

          const totalDays = endDate.diff(dayjs(segment.startTime).startOf('day'), 'day') + 1;
          if (totalDays > 1) {
            const currentDay = startDate.diff(dayjs(segment.startTime).startOf('day'), 'day') + 1;
            acc[elemKey].workTimes.push({
              workTime: segment,
              currentOfTotalDays: `${currentDay}/${totalDays}`,
            });
          } else {
            acc[elemKey].workTimes.push({ workTime: elem, currentOfTotalDays: '' });
          }

          acc[elemKey].workTimes.sort(
            (a, b) =>
              new Date(a.workTime.startTime).valueOf() - new Date(b.workTime.endTime).valueOf(),
          );

          startDate = startDate.add(1, 'day');
        }
      });
      return acc;
    }, {}),
  ).sort((a, b) => new Date(a.date).valueOf() - new Date(b.date).valueOf());
};
export const groupWorkTimesForDailyView = (
  calendarData: IWorkTime[],
  groupByDay?: boolean,
): IGroupedWorkTimes[] =>
  Object.values(
    calendarData.reduce((acc: Record<string, IGroupedWorkTimes>, elem: IWorkTime) => {
      const segments = groupByDay ? splitWorkTimeByDay(elem) : [elem];

      segments.forEach((segment) => {
        const elemKey = dayjs(segment.startTime).format('YYYY-MM-DD');
        if (!acc[elemKey]) {
          acc[elemKey] = {
            date: dayjs(segment.startTime).toISOString(),
            workTimes: [],
            amount: 0,
            totalDuration: { value: '0', unit: 'minutes' },
          };
        }

        const duration = calculateDuration(segment.startTime, segment.endTime);

        // Convert the existing total duration to minutes
        let totalMinutes = parseInt(acc[elemKey].totalDuration.value, 10);
        if (acc[elemKey].totalDuration.unit === 'hours') {
          const [hours, minutes] = acc[elemKey].totalDuration.value.split(':').map(Number);
          totalMinutes = hours * 60 + (minutes || 0);
        }

        // Convert the new duration to minutes and add to the total
        let newDurationMinutes = parseInt(duration.value, 10);
        if (duration.unit === 'hours') {
          const [hours, minutes] = duration.value.split(':').map(Number);
          newDurationMinutes = hours * 60 + (minutes || 0);
        }

        totalMinutes += newDurationMinutes;

        // Convert the total minutes back to hours and minutes
        if (totalMinutes < 60) {
          acc[elemKey].totalDuration = { value: totalMinutes.toString(), unit: 'minutes' };
        } else {
          const totalHours = Math.floor(totalMinutes / 60);
          const remainingMinutes = totalMinutes % 60;
          if (remainingMinutes === 0) {
            acc[elemKey].totalDuration = { value: totalHours.toString(), unit: 'hours' };
          } else {
            acc[elemKey].totalDuration = {
              value: `${totalHours}:${remainingMinutes < 10 ? '0' : ''}${remainingMinutes}`,
              unit: 'hours',
            };
          }
        }

        if (totalMinutes >= 60) {
          acc[elemKey].totalDuration.unit = 'hours';
        } else {
          acc[elemKey].totalDuration.unit = 'minutes';
        }

        acc[elemKey].amount += 1;
        acc[elemKey].workTimes.push({ workTime: segment, currentOfTotalDays: '' });
        acc[elemKey].workTimes.sort(
          (a, b) =>
            new Date(a.workTime.startTime).valueOf() - new Date(b.workTime.startTime).valueOf(),
        );
      });

      return acc;
    }, {}),
  ).sort((a, b) => new Date(a.date).valueOf() - new Date(b.date).valueOf());

const splitWorkTimeByDay = (workTime: IWorkTime): IWorkTime[] => {
  const segments: IWorkTime[] = [];
  let currentStartTime = dayjs(workTime?.startTime);
  const endTime = dayjs(workTime?.endTime);

  while (currentStartTime.isBefore(endTime)) {
    const nextDayStart = currentStartTime.clone().endOf('day').add(1, 'millisecond');
    const segmentEndTime = dayjs.min(nextDayStart, endTime);
    segments.push({
      ...workTime,
      startTime: currentStartTime.toISOString(),
      endTime: segmentEndTime.subtract(1, 'millisecond').toISOString(),
    });

    currentStartTime = segmentEndTime;
  }

  if (currentStartTime.isSame(endTime) && !segments.some((segment) => segment.id === workTime.id)) {
    segments.push({
      ...workTime,
      startTime: currentStartTime.toISOString(),
      endTime: endTime.toISOString(),
    });
  }

  return segments;
};
export const getWorkTimeDuration = (startTime: string, endTime: string) => {
  const { value, unit } = calculateDuration(startTime, endTime);

  if (unit === 'minutes') {
    return `${value} m`;
  }

  return `${value} h`;
};
export function getFormattedWorkTimeDuration(startDateTime?: Dayjs, endDateTime?: Dayjs) {
  const totalMinutes =
    startDateTime && endDateTime ? Math.floor(endDateTime.diff(startDateTime, 'minute', true)) : 0;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.floor(totalMinutes % 60);

  const formattedTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

  return { totalMinutes, formattedTime };
}

export const WORK_TIME_QUERY_KEYS_TO_INVALIDATE = ['worktime-list'];
